import React, { useState } from 'react';
import { ArrowLeft, X, Send, User, MapPin, Phone, CheckCircle, ShoppingCart, Trash2, Calendar, Clock, Printer, Plus, Minus } from 'lucide-react';
import CustomDatePicker from '../../admin/components/shared/CustomDatePicker';
import { useCart } from '../context/CartContext';
import { orderService, customerService } from '../../../services';
import { publicCustomerService } from '../../../services/api/publicCustomerService';
import ConfirmationModal from '../../../components/common/Modal/ConfirmationModal';

const CartDrawer = ({ isOpen, onClose, onSubmitOrder, sessionData, customerData, supplierData, token, deliverySchedules: propSchedules }) => {
    const { cart, totalAmount, clearCart, deleteItem, addToCart, removeFromCart, setQuantity, subTotalExVat, totalVat, grandTotal, totalSavings } = useCart();
    const [step, setStep] = useState('checkout'); // checkout, success
    const [isEditingDetails, setIsEditingDetails] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [customerDetails, setCustomerDetails] = useState({
        name: '',
        mobile: '',
        location: ''
    });

    // Delivery details state - Default to Tomorrow, no localStorage persistence
    const [deliveryDate, setDeliveryDate] = useState(() => {
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        return tomorrow.toISOString().split('T')[0];
    });
    const [timeSlot, setTimeSlot] = useState('');
    const [printFormat, setPrintFormat] = useState('a4'); // 'a4' or 'thermal'
    const [orderResponse, setOrderResponse] = useState(null);
    const [isCopied, setIsCopied] = useState(false);
    const [isClearCartModalOpen, setIsClearCartModalOpen] = useState(false);
    const [itemToDelete, setItemToDelete] = useState(null);
    const [isSavingProfile, setIsSavingProfile] = useState(false);
    const [isEditingContact, setIsEditingContact] = useState(false);
    const [isSavingContact, setIsSavingContact] = useState(false);

    // Save profile helper to persist delivery address and schedule to DB
    const handleSaveProfile = async (updatedLocation, updatedTimeSlot) => {
        if (!token) return;
        setIsSavingProfile(true);
        try {
            await publicCustomerService.updateCustomerProfile(token, {
                address: updatedLocation,
                preferredTimeSlot: updatedTimeSlot
            });
            console.log('[CartDrawer] Customer profile details persisted in DB');
        } catch (error) {
            console.error('[CartDrawer] Failed to persist profile details to DB:', error);
        } finally {
            setIsSavingProfile(false);
        }
    };

    // Save contact details helper to persist name and mobile number to DB
    const handleSaveContact = async (updatedName, updatedMobile) => {
        if (!token) return;
        setIsSavingContact(true);
        try {
            await publicCustomerService.updateCustomerProfile(token, {
                name: updatedName,
                mobileNumber: updatedMobile
            });
            console.log('[CartDrawer] Customer contact details persisted in DB');
        } catch (error) {
            console.error('[CartDrawer] Failed to persist contact details to DB:', error);
            alert("Failed to save contact details. Please try again.");
        } finally {
            setIsSavingContact(false);
        }
    };

    const handleTimeSlotChange = (newTimeSlot) => {
        setTimeSlot(newTimeSlot);
        handleSaveProfile(customerDetails.location, newTimeSlot);
    };

    // Pre-fill customer details from fetched customer data
    React.useEffect(() => {
        if (customerData) {
            console.log('[CartDrawer] Pre-filling customer details from backend:', customerData);
            setCustomerDetails({
                name: customerData.name || customerData.customerName || customerData.contactPerson || '',
                mobile: customerData.mobile || customerData.mobileNumber || customerData.whatsappNumber || '',
                location: customerData.address || ''
            });

            // Set time slot to preferred value if present in customerData
            if (customerData.preferredTimeSlot) {
                setTimeSlot(customerData.preferredTimeSlot);
            } else {
                const schedules = propSchedules || [];
                const defaultSchedule = schedules.find(s => s.default || s.isDefault);
                if (defaultSchedule) {
                    setTimeSlot(defaultSchedule.name);
                } else if (schedules.length > 0) {
                    setTimeSlot(schedules[0].name);
                }
            }
        }
    }, [customerData, propSchedules]);

    // Log customer and supplier data when available
    React.useEffect(() => {
        if (customerData) {
            console.log('[CartDrawer] Customer data available:', customerData);
        }
        if (supplierData) {
            console.log('[CartDrawer] Supplier data available:', supplierData);
        }
    }, [customerData, supplierData]);

    // Reset state when drawer closes
    React.useEffect(() => {
        if (!isOpen) {
            // Use a slight timeout to avoid visual jump during slide animation
            const timer = setTimeout(() => {
                setStep('checkout');
                setIsEditingDetails(false);
                setIsEditingContact(false);
                setOrderResponse(null);
                setIsCopied(false);
            }, 400);
            return () => clearTimeout(timer);
        }
    }, [isOpen]);

    if (!isOpen) return null;

    const formatDate = () => {
        const now = new Date();
        return now.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) +
            ', ' +
            now.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
    };

    const hasValidDetails = () => {
        const nameVal = customerDetails?.name?.trim();
        const mobileVal = customerDetails?.mobile;
        const locationVal = customerDetails?.location?.trim();
        const mobileRegex = /^[\d\s\-\+]{9,}$/;
        return !!(nameVal && mobileVal && mobileRegex.test(mobileVal) && locationVal);
    };

    const getValidationWarning = () => {
        const nameVal = customerDetails?.name?.trim();
        const mobileVal = customerDetails?.mobile;
        const locationVal = customerDetails?.location?.trim();
        const mobileRegex = /^[\d\s\-\+]{9,}$/;

        if (!nameVal) {
            return "Contact Details: Contact name is required.";
        }
        if (!mobileVal) {
            return "Contact Details: Mobile number is required.";
        }
        if (!mobileRegex.test(mobileVal)) {
            return "Contact Details: Mobile number must be a valid phone number (at least 9 digits).";
        }
        if (!locationVal) {
            return "Delivery Address: Delivery address is required.";
        }
        return "Please complete your delivery details to place the order.";
    };

    const validateDetails = () => {
        if (cart.length === 0) {
            alert("Your cart is empty");
            return false;
        }
        if (!customerDetails.name.trim()) {
            alert("Please enter your name");
            return false;
        }
        const mobileRegex = /^[\d\s\-\+]{9,}$/;
        if (!mobileRegex.test(customerDetails.mobile)) {
            alert("Please enter a valid mobile number (min 9 digits)");
            return false;
        }
        if (!customerDetails.location.trim()) {
            alert("Please enter your delivery location");
            return false;
        }
        return true;
    };

    const handleDetailsSubmit = (e) => {
        e.preventDefault();
        if (validateDetails()) {
            setStep('review');
        }
    };

    const handleFinalSubmit = async () => {
        if (cart.length === 0) {
            alert("Your cart is empty");
            return;
        }
        setIsSubmitting(true);
        try {
            // If onSubmitOrder callback is provided, use it (public API flow)
            if (onSubmitOrder) {
                // Persistent updates safety net before checkout completes
                if (token && customerData && (customerDetails.location !== customerData.address || timeSlot !== customerData.preferredTimeSlot)) {
                    try {
                        await publicCustomerService.updateCustomerProfile(token, {
                            address: customerDetails.location,
                            preferredTimeSlot: timeSlot
                        });
                    } catch (profileError) {
                        console.error('[CartDrawer] Safety-net profile update failed:', profileError);
                    }
                }

                const isTrading = customerData?.isTrading !== false && customerData?.trading !== false;
                const response = await onSubmitOrder({
                    items: cart,
                    deliveryDate: isTrading ? (deliveryDate || null) : null,
                    timeSlot: isTrading ? (timeSlot || null) : null
                });

                console.log('[CartDrawer] Order placed response:', response);

                // Ensure we handle various response shapes if needed, but assuming flat object based on publicOrderService
                setOrderResponse(response);
                setStep('success');
                clearCart();
            } else {
                // Legacy flow (authenticated customer)
                await customerService.createCustomer(customerDetails);

                const orderData = {
                    customer: {
                        name: customerDetails.name,
                        phone: customerDetails.mobile,
                        location: customerDetails.location
                    },
                    items: cart,
                    totalAmount: totalAmount,
                    salesman: 'Direct Customer',
                    salesManager: 'Direct',
                    status: 'new',
                    isPos: false,
                    source: 'web_link'
                };

                await orderService.placeOrder(orderData);

                // Generate WhatsApp Link and Open
                const message = `*New Order from ${customerDetails.name}*
📍 ${customerDetails.location}
📞 ${customerDetails.mobile}

*Order Items:*
${cart.map(item => `• ${item.name} x ${item.quantity} (${item.unit || item.defaultUnit}) - AED ${(item.price * item.quantity).toFixed(2)}`).join('\n')}

*Total Amount: AED ${totalAmount}*`;

                const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(message)}`;
                window.open(whatsappUrl, '_blank');

                setStep('success');
                clearCart();
            }
        } catch (error) {
            console.error("Order failed", error);
            alert("Failed to place order. Please try again.");
        } finally {
            setIsSubmitting(false);
        }
    };

    const renderCheckoutStep = () => {
        if (cart.length === 0) {
            return (
                <div className="flex-1 flex flex-col items-center justify-center p-8 text-center bg-gray-50">
                    <div className="w-20 h-20 bg-gray-200 rounded-full flex items-center justify-center mb-4">
                        <ShoppingCart className="w-10 h-10 text-gray-400" />
                    </div>
                    <h3 className="text-lg font-bold text-gray-800 mb-2">Your cart is empty</h3>
                    <p className="text-gray-500 mb-8 max-w-[200px]">Looks like you haven't added any items yet.</p>
                    <button
                        onClick={onClose}
                        className="bg-cyan-500 text-white px-8 py-3 rounded-xl font-bold hover:bg-cyan-600 transition-colors shadow-lg shadow-cyan-500/30"
                    >
                        Start Shopping
                    </button>
                </div>
            );
        }

        return (
            <div className="flex-1 flex flex-col min-h-0 bg-gray-50">
                {/* Warning notice if details are incomplete */}
                {!hasValidDetails() && (
                    <div className="p-3.5 bg-yellow-50 border-b border-yellow-100 flex-shrink-0">
                        <p className="text-xs text-yellow-800 flex items-start gap-2 font-bold">
                            <User size={14} className="text-yellow-600 mt-0.5 flex-shrink-0" />
                            <span>{getValidationWarning()}</span>
                        </p>
                    </div>
                )}

                <div className="flex-1 overflow-y-auto p-4 space-y-4">
                    {/* Cart Items List */}
                    <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100/50">
                        <div className="flex items-center justify-between mb-3 border-b border-slate-50 pb-2.5">
                            <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider flex items-center gap-2">
                                <ShoppingCart size={14} />
                                Order Items ({cart.length})
                            </h3>
                            <button
                                onClick={() => setIsClearCartModalOpen(true)}
                                className="text-xs text-red-600 hover:text-red-700 font-bold flex items-center gap-1 hover:bg-red-50 px-2 py-1 rounded transition-colors"
                            >
                                <Trash2 size={13} />
                                Clear
                            </button>
                        </div>
                        <div className="space-y-3 max-h-56 overflow-y-auto custom-scrollbar pr-0.5 divide-y divide-slate-50">
                            {cart.map((item, idx) => (
                                <div key={item.id} className={`group flex items-center justify-between ${idx > 0 ? 'pt-3' : ''}`}>
                                    <div className="flex-1 min-w-0 pr-2">
                                        <p className="font-bold text-gray-800 text-xs break-words whitespace-normal line-clamp-2" title={item.name}>{item.name}</p>
                                        <p className="text-[10px] text-gray-500 font-semibold mt-0.5 flex items-center gap-1.5">
                                            <span className="inline-block px-1.5 py-0.5 bg-gray-100 text-gray-600 rounded text-[9px] font-bold">
                                                {item.unit || item.defaultUnit}
                                            </span>
                                        </p>
                                    </div>
                                    <div className="flex items-center gap-2 flex-shrink-0">
                                        {/* Inline Quantity Controls */}
                                        <div className="flex items-center gap-1 bg-gray-50 rounded-lg px-1.5 py-1 border border-gray-200">
                                            <button
                                                onClick={() => removeFromCart(item.id)}
                                                className="w-5 h-5 flex items-center justify-center rounded text-cyan-500 hover:bg-gray-100"
                                            >
                                                <Minus className="w-3.5 h-3.5" />
                                            </button>
                                            <input
                                                type="number"
                                                min="1"
                                                value={item.quantity || ''}
                                                onChange={(e) => {
                                                    const val = e.target.value;
                                                    if (val === '') {
                                                        setQuantity(item, 0);
                                                        return;
                                                    }
                                                    const parsed = parseInt(val, 10);
                                                    if (!isNaN(parsed) && parsed >= 0) {
                                                        setQuantity(item, parsed);
                                                    }
                                                }}
                                                className="w-8 text-center font-bold text-gray-800 text-xs bg-transparent border-none outline-none focus:ring-0 p-0 m-0 hide-number-spinners"
                                            />
                                            <button
                                                onClick={() => addToCart(item)}
                                                className="w-5 h-5 flex items-center justify-center rounded text-cyan-500 hover:bg-gray-100"
                                            >
                                                <Plus className="w-3.5 h-3.5" />
                                            </button>
                                        </div>

                                        <span className="font-black text-gray-800 text-xs min-w-[70px] text-right">
                                            AED {(item.price * item.quantity).toFixed(2)}
                                        </span>
                                        <button
                                            onClick={() => setItemToDelete(item)}
                                            className="p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all"
                                            title="Remove Item"
                                        >
                                            <Trash2 size={14} />
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Delivery Contact Info Section */}
                    <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100/50">
                        <div className="flex items-center justify-between mb-3">
                            <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider flex items-center gap-2">
                                <User size={14} />
                                Contact Details
                            </h3>
                            {!isEditingContact && (
                                <button
                                    onClick={() => setIsEditingContact(true)}
                                    className="text-xs font-bold text-cyan-600 hover:text-cyan-700 transition-colors"
                                >
                                    Edit
                                </button>
                            )}
                        </div>

                        {isEditingContact ? (
                            <div className="space-y-3">
                                <div>
                                    <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">Contact Name</label>
                                    <input
                                        type="text"
                                        className="w-full p-2.5 bg-gray-50 border border-gray-200 rounded-xl text-xs focus:outline-none focus:ring-2 focus:ring-cyan-500 transition-all font-semibold"
                                        placeholder="Name"
                                        value={customerDetails.name}
                                        onChange={e => setCustomerDetails({ ...customerDetails, name: e.target.value })}
                                    />
                                </div>
                                <div>
                                    <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">Mobile Number</label>
                                    <input
                                        type="text"
                                        className="w-full p-2.5 bg-gray-50 border border-gray-200 rounded-xl text-xs focus:outline-none focus:ring-2 focus:ring-cyan-500 transition-all font-semibold"
                                        placeholder="Mobile Number"
                                        value={customerDetails.mobile}
                                        onChange={e => setCustomerDetails({ ...customerDetails, mobile: e.target.value })}
                                    />
                                </div>
                                <div className="flex gap-2">
                                    <button
                                        type="button"
                                        disabled={!customerDetails.name?.trim() || !customerDetails.mobile?.trim() || isSavingContact}
                                        onClick={async () => {
                                            const mobileRegex = /^[\d\s\-\+]{9,}$/;
                                            if (!mobileRegex.test(customerDetails.mobile)) {
                                                alert("Please enter a valid mobile number (min 9 digits)");
                                                return;
                                            }
                                            setIsEditingContact(false);
                                            await handleSaveContact(customerDetails.name, customerDetails.mobile);
                                        }}
                                        className="flex-1 bg-cyan-500 text-white py-2 rounded-xl text-xs font-bold hover:bg-cyan-600 transition-colors disabled:bg-gray-200 disabled:text-gray-400"
                                    >
                                        {isSavingContact ? 'Saving...' : 'Save Details'}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setIsEditingContact(false);
                                            if (customerData) {
                                                setCustomerDetails(prev => ({
                                                    ...prev,
                                                    name: customerData.name || customerData.customerName || customerData.contactPerson || '',
                                                    mobile: customerData.mobile || customerData.mobileNumber || customerData.whatsappNumber || ''
                                                }));
                                            }
                                        }}
                                        className="px-4 bg-slate-100 text-slate-700 py-2 rounded-xl text-xs font-bold hover:bg-slate-200 transition-colors"
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <div className="space-y-2 text-xs">
                                <div className="flex items-start gap-3">
                                    <User className="w-4 h-4 text-gray-400 mt-0.5" />
                                    <div>
                                        <p className="font-bold text-gray-900">{customerDetails.name}</p>
                                    </div>
                                </div>
                                <div className="flex items-start gap-3">
                                    <Phone className="w-4 h-4 text-gray-400 mt-0.5" />
                                    <div>
                                        <p className="text-gray-600 font-medium">{customerDetails.mobile}</p>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Delivery Location Section */}
                    <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100/50">
                        <div className="flex items-center justify-between mb-3">
                            <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider flex items-center gap-2">
                                <MapPin size={14} />
                                Delivery Address
                            </h3>
                            {!isEditingDetails && (
                                <button
                                    onClick={() => setIsEditingDetails(true)}
                                    className="text-xs font-bold text-cyan-600 hover:text-cyan-700 transition-colors"
                                >
                                    Edit
                                </button>
                            )}
                        </div>

                        {isEditingDetails || !customerDetails.location ? (
                            <div className="space-y-3">
                                <div>
                                    <input
                                        type="text"
                                        className="w-full p-2.5 bg-gray-50 border border-gray-200 rounded-xl text-xs focus:outline-none focus:ring-2 focus:ring-cyan-500 transition-all font-semibold"
                                        placeholder="e.g. Villa 23, Dubai"
                                        value={customerDetails.location}
                                        onChange={e => setCustomerDetails({ ...customerDetails, location: e.target.value })}
                                    />
                                </div>
                                <div className="flex gap-2">
                                    <button
                                        type="button"
                                        disabled={!customerDetails.location?.trim() || isSavingProfile}
                                        onClick={async () => {
                                            setIsEditingDetails(false);
                                            await handleSaveProfile(customerDetails.location, timeSlot);
                                        }}
                                        className="flex-1 bg-cyan-500 text-white py-2 rounded-xl text-xs font-bold hover:bg-cyan-600 transition-colors disabled:bg-gray-200 disabled:text-gray-400"
                                    >
                                        {isSavingProfile ? 'Saving...' : 'Save Address'}
                                    </button>
                                    {customerDetails.location && (
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setIsEditingDetails(false);
                                                // Reset to previous value
                                                if (customerData) {
                                                    setCustomerDetails(prev => ({
                                                        ...prev,
                                                        location: customerData.address || ''
                                                    }));
                                                }
                                            }}
                                            className="px-4 bg-slate-100 text-slate-700 py-2 rounded-xl text-xs font-bold hover:bg-slate-200 transition-colors"
                                        >
                                            Cancel
                                        </button>
                                    )}
                                </div>
                            </div>
                        ) : (
                            <div className="space-y-2 text-xs">
                                <div className="flex items-start gap-3">
                                    <MapPin className="w-4 h-4 text-gray-400 mt-0.5" />
                                    <div>
                                        <p className="text-gray-600 font-medium leading-relaxed">{customerDetails.location}</p>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Delivery Schedule Section - Rendered only for trading customers */}
                    {customerData?.isTrading !== false && customerData?.trading !== false && (
                        <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100/50">
                            <h3 className="text-xs font-bold text-gray-400 mb-3 uppercase tracking-wider flex items-center gap-2">
                                <Clock size={14} />
                                Delivery Schedule
                            </h3>
                            <div className="space-y-3">
                                <div>
                                    <label className="block text-xs font-bold text-gray-500 mb-1">Delivery Date</label>
                                    <CustomDatePicker
                                        mode="single"
                                        value={deliveryDate}
                                        onChange={(val) => setDeliveryDate(val)}
                                        quickFilters={false}
                                        className="w-full"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-gray-500 mb-1">Time Slot</label>
                                    <select
                                        className="w-full p-2.5 bg-gray-50 border border-gray-200 rounded-xl text-xs focus:outline-none focus:ring-2 focus:ring-cyan-500 transition-all font-semibold cursor-pointer"
                                        value={timeSlot}
                                        onChange={(e) => handleTimeSlotChange(e.target.value)}
                                    >
                                        {(propSchedules || []).length > 0 ? (
                                            (propSchedules || []).map(schedule => (
                                                <option key={schedule.publicId} value={schedule.name}>
                                                    {schedule.name}{schedule.startTime && schedule.endTime ? ` (${schedule.startTime} – ${schedule.endTime})` : ''}
                                                </option>
                                            ))
                                        ) : (
                                            <>
                                                <option value="" className="text-gray-400">Select a time slot</option>
                                                <option value="Morning">Morning</option>
                                                <option value="Afternoon">Afternoon</option>
                                                <option value="Evening">Evening</option>
                                            </>
                                        )}
                                    </select>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Subtotal & Totals Footer */}
                <div className="border-t bg-white p-4 pb-8 sm:pb-4 space-y-2 flex-shrink-0 shadow-lg">
                    <div className="space-y-1 px-2 text-xs">
                        <div className="flex justify-between text-gray-500 font-semibold">
                            <span>Subtotal (Excl. VAT)</span>
                            <span>AED {subTotalExVat}</span>
                        </div>
                        <div className="flex justify-between text-gray-500 font-semibold pb-1.5 border-b border-dashed border-gray-150">
                            <span>Total VAT</span>
                            <span>AED {totalVat}</span>
                        </div>
                        {parseFloat(totalSavings) > 0 && (
                            <div className="flex justify-between text-emerald-600 font-extrabold bg-emerald-50 border border-emerald-100/50 px-2.5 py-1.5 rounded-xl text-xs mt-1 animate-pulse">
                                <span>You have saved</span>
                                <span>AED {totalSavings}</span>
                            </div>
                        )}
                    </div>
                    <div className="flex justify-between items-center bg-slate-50/60 px-4 py-2.5 rounded-xl">
                        <div>
                            <p className="text-[10px] font-black text-slate-400 uppercase tracking-wider mb-0.5">Total Amount</p>
                            <p className="text-[10px] text-slate-500 font-semibold">Net Payable (Incl. VAT)</p>
                        </div>
                        <span className="font-black text-lg text-cyan-600">AED {grandTotal}</span>
                    </div>
                    <div className="flex gap-3">
                        <button
                            type="button"
                            onClick={onClose}
                            className="flex-1 py-3 border border-gray-200 bg-white hover:bg-gray-50 text-gray-700 font-bold rounded-xl text-xs transition-all flex items-center justify-center gap-2 shadow-sm"
                        >
                            Add More Items
                        </button>
                        <button
                            onClick={handleFinalSubmit}
                            disabled={isSubmitting || !hasValidDetails()}
                            className={`flex-[2] py-3 rounded-xl font-bold transition-all flex items-center justify-center gap-2 text-xs shadow-md ${
                                isSubmitting || !hasValidDetails()
                                    ? 'bg-slate-200 text-slate-400 cursor-not-allowed shadow-none'
                                    : 'bg-cyan-500 text-white hover:bg-cyan-600 shadow-cyan-500/20 active:scale-[0.98]'
                            }`}
                        >
                            <Send className="w-3.5 h-3.5" />
                            {isSubmitting 
                                ? 'Processing...' 
                                : (onSubmitOrder ? 'Place Order' : 'Send Order to WhatsApp')}
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    const renderSuccessStep = () => (
        <div className="flex-1 overflow-y-auto flex flex-col items-center p-8 text-center animate-fade-in no-scrollbar">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mb-6">
                <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Order Placed!</h2>

            {orderResponse && (orderResponse.orderRefNo || orderResponse.publicId) && (
                <div className="bg-cyan-50 border border-cyan-200 rounded-lg p-4 mb-4 w-full max-w-sm relative group">
                    <div className="flex items-center justify-center gap-2">
                        <p className="text-xl font-bold text-cyan-600 tracking-wider font-mono select-all" title="Click to select">
                            {orderResponse.orderRefNo || orderResponse.orderNumber || orderResponse.publicId}
                        </p>
                        <button
                            onClick={() => {
                                const refToCopy = orderResponse.orderRefNo || orderResponse.orderNumber || orderResponse.publicId;
                                navigator.clipboard.writeText(refToCopy);
                                setIsCopied(true);
                                setTimeout(() => setIsCopied(false), 2000);
                            }}
                            className="p-1.5 hover:bg-cyan-100 rounded-lg transition-colors text-cyan-600 flex items-center gap-1 min-w-[32px]"
                            title="Copy Order ID"
                        >
                            {isCopied ? (
                                <CheckCircle size={16} className="text-green-600" />
                            ) : (
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                                    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                                </svg>
                            )}
                        </button>
                    </div>
                </div>
            )}

            {parseFloat(totalSavings) > 0 && (
                <div className="bg-emerald-50 border border-emerald-100 rounded-2xl p-4 mb-5 w-full max-w-sm text-center shadow-sm">
                    <p className="text-sm font-bold text-emerald-800">
                        🎉 You have saved AED {totalSavings} on this order!
                    </p>
                </div>
            )}

            <p className="text-gray-500 mb-2">
                Thank you, {customerDetails.name}!
            </p>
            <p className="text-sm text-gray-400 mb-8">
                We have received your order and will contact you shortly at {customerDetails.mobile}.
            </p>

            {orderResponse && (deliveryDate || timeSlot) && (
                <div className="bg-gray-50 rounded-lg p-4 mb-6 w-full max-w-sm text-center">
                    <p className="text-xs font-bold text-gray-500 mb-2.5 uppercase tracking-wider">Delivery Details</p>
                    <div className="flex flex-col items-center gap-2">
                        {deliveryDate && (
                            <p className="text-sm text-gray-700 flex items-center gap-2 font-semibold justify-center">
                                <Calendar className="w-4 h-4 text-cyan-600" />
                                {new Date(deliveryDate).toLocaleDateString('en-US', {
                                    weekday: 'short',
                                    year: 'numeric',
                                    month: 'short',
                                    day: 'numeric'
                                })}
                            </p>
                        )}
                        {timeSlot && (
                            <p className="text-sm text-gray-700 flex items-center gap-2 font-semibold justify-center">
                                <Clock className="w-4 h-4 text-cyan-600" />
                                {timeSlot}
                            </p>
                        )}
                    </div>
                </div>
            )}

            <button
                onClick={() => {
                    onClose();
                    setTimeout(() => {
                        setStep('checkout');
                        setOrderResponse(null);
                    }, 300); // Reset after close
                }}
                className="bg-gray-100 text-gray-800 px-8 py-3 rounded-xl font-bold hover:bg-gray-200 transition-colors w-full mb-3"
            >
                Continue Shopping
            </button>

            <div className="flex flex-col items-center gap-2.5 w-full border-t border-gray-100 pt-5 mt-4">
                <div className="flex items-center justify-between w-full max-w-xs gap-3">
                    <select
                        value={printFormat}
                        onChange={(e) => setPrintFormat(e.target.value)}
                        className="flex-1 px-3 py-2 rounded-xl text-xs font-semibold bg-gray-50 border border-gray-200 outline-none focus:ring-2 focus:ring-cyan-500 cursor-pointer text-gray-700"
                    >
                        <option value="a4">Standard A4 Invoice</option>
                        <option value="thermal">4" Thermal Receipt</option>
                    </select>
                    <button
                        onClick={() => {
                            if (!orderResponse) return;

                            // Save data for the print page
                            // Extract sales person from customer data if not in order response (frontend fallback)
                            const primarySalesPerson = customerData?.salesPersons?.[0];
                            const enrichedOrder = {
                                ...orderResponse,
                                deliveryDate: orderResponse.deliveryDate || deliveryDate,
                                timeSlot: orderResponse.timeSlot || timeSlot,
                                assignedSalesPersonName: orderResponse.assignedSalesPersonName || primarySalesPerson?.fullName || primarySalesPerson?.name,
                                assignedSalesPersonMobile: orderResponse.assignedSalesPersonMobile || primarySalesPerson?.mobile || primarySalesPerson?.phone,

                                // Robustly merge customer details: use response if available, backup with local details
                                customer: {
                                    ...(orderResponse.customer || {}),
                                    ...customerDetails,
                                    name: orderResponse.customer?.name || customerData?.customerName || customerDetails.name,
                                    mobile: orderResponse.customer?.mobile || customerData?.mobileNumber || customerDetails.mobile,
                                    address: orderResponse.customer?.address || customerData?.address || customerDetails.location
                                }
                            };

                            // Save data for the print page
                            const printData = {
                                order: enrichedOrder,
                                supplier: {
                                    ...supplierData,
                                    businessName: supplierData?.businessName || sessionData?.tenant?.businessName,
                                    address: supplierData?.address || sessionData?.tenant?.address,
                                    phone: supplierData?.phone || sessionData?.tenant?.phone,
                                    email: supplierData?.email || sessionData?.tenant?.email
                                },
                                customer: enrichedOrder.customer // Explicitly pass enriched customer details as backup
                            };

                            localStorage.setItem('printInvoiceData', JSON.stringify(printData));

                            // Trigger print via hidden iframe to stay on same page
                            const printFrame = document.createElement('iframe');
                            printFrame.style.position = 'absolute';
                            printFrame.style.top = '-9999px';
                            printFrame.style.left = '-9999px';
                            printFrame.style.width = '0px';
                            printFrame.style.height = '0px';
                            printFrame.src = `/print-order?format=${printFormat}`;

                            document.body.appendChild(printFrame);

                            // Cleanup iframe after printing (PrintOrderPage triggers print automatically)
                            // We'll leave it for a bit to ensure print dialog opens, then remove
                            setTimeout(() => {
                                document.body.removeChild(printFrame);
                            }, 60000); // 1 minute timeout cleanup (print dialog blocks script execution in some browsers)
                        }}
                        className="bg-cyan-600 hover:bg-cyan-700 text-white font-bold text-xs px-4 py-2 rounded-xl transition-all flex items-center justify-center gap-1.5 shadow-sm active:scale-95"
                    >
                        <Printer size={14} />
                        Print
                    </button>
                </div>
            </div>
        </div>
    );

    return (
        <div 
            className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-50 print:hidden"
            onClick={onClose}
        >
            <div 
                className="bg-white rounded-t-3xl sm:rounded-3xl w-full max-w-md h-[85vh] sm:h-auto sm:max-h-[90vh] overflow-hidden flex flex-col animate-slide-up shadow-2xl"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b bg-gray-50/80 backdrop-blur-md flex-shrink-0 sticky top-0 z-10">
                    <div className="flex items-center gap-3">
                        <h2 className="text-lg font-bold text-gray-900 tracking-tight">
                            {step === 'checkout' ? 'Shopping Bag & Checkout' : 'Order Confirmed'}
                        </h2>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-gray-200/50 rounded-full transition-colors active:scale-95"
                    >
                        <X className="w-5 h-5 text-gray-500" />
                    </button>
                </div>

                {step === 'checkout' && renderCheckoutStep()}
                {step === 'success' && renderSuccessStep()}

            </div>

            {/* Confirmation Modals */}
            <ConfirmationModal
                isOpen={isClearCartModalOpen}
                onClose={() => setIsClearCartModalOpen(false)}
                onConfirm={() => {
                    clearCart();
                    setIsClearCartModalOpen(false);
                }}
                title="Clear Shopping Bag?"
                message="Are you sure you want to remove all items from your shopping bag? This action cannot be undone."
                confirmText="Yes, Clear Bag"
                cancelText="No, Keep Items"
                type="danger"
                icon={ShoppingCart}
            />

            <ConfirmationModal
                isOpen={!!itemToDelete}
                onClose={() => setItemToDelete(null)}
                onConfirm={() => {
                    if (itemToDelete) {
                        deleteItem(itemToDelete.id);
                        setItemToDelete(null);
                    }
                }}
                title="Remove Item?"
                message={`Are you sure you want to remove "${itemToDelete?.name}" from your shopping bag?`}
                confirmText="Remove Item"
                type="danger"
                icon={Trash2}
            />

            <style>{`
                @keyframes slide-up {
                    from { transform: translateY(100%); }
                    to { transform: translateY(0); }
                }
                .animate-slide-up {
                    animation: slide-up 0.4s cubic-bezier(0.16, 1, 0.3, 1);
                }
                 @keyframes fade-in {
                    from { opacity: 0; transform: scale(0.95); }
                    to { opacity: 1; transform: scale(1); }
                }
                .animate-fade-in {
                    animation: fade-in 0.3s cubic-bezier(0.16, 1, 0.3, 1);
                }
                /* Hide scrollbar for Chrome, Safari and Opera */
                .no-scrollbar::-webkit-scrollbar {
                    display: none;
                }
                /* Hide scrollbar for IE, Edge and Firefox */
                .no-scrollbar {
                    -ms-overflow-style: none;  /* IE and Edge */
                    scrollbar-width: none;  /* Firefox */
                }
            `}</style>
        </div>
    );
};

export default CartDrawer;
